import * as Sentry from "@sentry/react";
import { lazy, type ReactElement, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { SentryErrorBoundary } from "@/components/SentryErrorBoundary";

const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

const AppLayout = lazy(() => import("@/app/(authenticated)/layout"));
const InventoryPage = lazy(
  () => import("@/app/(authenticated)/inventory/page"),
);
const InventoryDetailPage = lazy(
  () => import("@/app/(authenticated)/inventory/[id]/page"),
);
const JobsPage = lazy(() => import("@/app/(authenticated)/jobs/page"));
const JobsDetailPage = lazy(
  () => import("@/app/(authenticated)/jobs/[id]/page"),
);
const AdminUsersPage = lazy(
  () => import("@/app/(authenticated)/admin/users/page"),
);
const ProfilePage = lazy(() => import("@/app/(authenticated)/profile/page"));
const SettingsPage = lazy(() => import("@/app/(authenticated)/settings/page"));
const LoginPage = lazy(() => import("@/app/login/page"));
const ForgotPasswordPage = lazy(() => import("@/app/forgot-password/page"));
const ResetPasswordPage = lazy(() => import("@/app/reset-password/page"));
const HomePage = lazy(() => import("@/app/page"));
const ErrorPage = lazy(() => import("@/app/error/page"));

const routeSuspense = (element: ReactElement) => (
  <Suspense fallback={<PageLoader />}>{element}</Suspense>
);

function PageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <p className="text-sm text-muted-foreground">Loading page...</p>
    </div>
  );
}

export default function App() {
  return (
    <Sentry.ErrorBoundary
      onError={(error) => console.error("Sentry.ErrorBoundary caught:", error)}
      fallback={({ error, resetError }) => (
        <SentryErrorBoundary error={error as Error} reset={resetError} />
      )}
    >
      <SentryRoutes>
        <Route path="/" element={routeSuspense(<HomePage />)} />
        <Route path="/login" element={routeSuspense(<LoginPage />)} />
        <Route
          path="/forgot-password"
          element={routeSuspense(<ForgotPasswordPage />)}
        />
        <Route
          path="/reset-password"
          element={routeSuspense(<ResetPasswordPage />)}
        />
        <Route path="/error" element={routeSuspense(<ErrorPage />)} />
        <Route element={routeSuspense(<AppLayout />)}>
          <Route path="/inventory" element={routeSuspense(<InventoryPage />)} />
          <Route
            path="/inventory/:id"
            element={routeSuspense(<InventoryDetailPage />)}
          />
          <Route path="/jobs" element={routeSuspense(<JobsPage />)} />
          <Route path="/jobs/:id" element={routeSuspense(<JobsDetailPage />)} />
          <Route path="/profile" element={routeSuspense(<ProfilePage />)} />
          <Route path="/settings" element={routeSuspense(<SettingsPage />)} />
          <Route
            path="/admin/users"
            element={routeSuspense(<AdminUsersPage />)}
          />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </SentryRoutes>
    </Sentry.ErrorBoundary>
  );
}
